import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../db';
import { Trophy, Medal, Star, ChevronDown, ChevronUp, Copy, Check, UserPlus, UserCheck, UserX, Clock, Users, X } from 'lucide-react';
import { getLevelInfo, getGrade } from '../utils/xpUtils';
import { getEarnedBadges } from '../utils/badgeUtils';
import { toast } from '../utils/feedback';
import UserAvatar from '../components/UserAvatar';

const Leaderboard = ({ currentProfile }) => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProfileId, setExpandedProfileId] = useState(null);
  const [expandedData, setExpandedData] = useState({ workouts: [], routines: [], loading: false });
  const [clonedRoutines, setClonedRoutines] = useState({});
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [activeTab, setActiveTab] = useState('global');

  useEffect(() => {
    const fetchProfiles = async () => {
      const data = await db.getProfiles();
      const enrichedProfiles = data.map(p => {
        const xpInfo = getLevelInfo(p.xp || 0);
        return { ...p, level: xpInfo.level, grade: getGrade(xpInfo.level), totalXp: p.xp || 0 };
      });
      enrichedProfiles.sort((a, b) => b.totalXp - a.totalXp);
      setProfiles(enrichedProfiles);
      setLoading(false);
    };
    fetchProfiles();
  }, [currentProfile?.id]);

  const friendsList = currentProfile?.friends || [];
  const receivedRequests = currentProfile?.receivedRequests || [];
  const sentRequests = currentProfile?.sentRequests || [];

  useEffect(() => {
    if (selectedRoutine) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedRoutine]);

  const handleExpandProfile = async (profileId) => {
    if (expandedProfileId === profileId) {
      setExpandedProfileId(null);
      return;
    }
    setExpandedProfileId(profileId);
    setExpandedData({ workouts: [], routines: [], goals: [], loading: true });
    
    const [wData, rData, gData] = await Promise.all([
      db.getWorkouts(profileId),
      db.getRoutines(profileId),
      db.getGoals(profileId)
    ]);
    
    setExpandedData({ workouts: wData, routines: rData, goals: gData, loading: false });
  };

  const handleCloneRoutine = async (routine) => {
    if (!currentProfile) return;
    const { id, profileId, ...rest } = routine;
    await db.addRoutine({
      ...rest,
      profileId: currentProfile.id
    });
    setClonedRoutines({ ...clonedRoutines, [routine.id]: true });
    toast.success("Programme cloné avec succès ! Il est maintenant disponible dans tes Programmes.");
  };

  const handleSendRequest = async (e, profileId) => {
    e.stopPropagation();
    if(!currentProfile) return;
    await db.sendFriendRequest(currentProfile.id, profileId);
    toast.success("Demande d'ami envoyée !");
  };

  const handleCancelRequest = async (e, profileId) => {
    e.stopPropagation();
    if(!currentProfile) return;
    await db.cancelFriendRequest(currentProfile.id, profileId);
    toast.success("Demande annulée.");
  };

  const handleRemoveFriend = async (e, profileId) => {
    e.stopPropagation();
    if(!currentProfile) return;
    if(window.confirm("Veux-tu vraiment retirer cet ami ?")) {
      await db.removeFriend(currentProfile.id, profileId);
      toast.success("Ami retiré.");
    }
  };

  if (loading) return <div className="text-center mt-3">Chargement du classement...</div>;

  let displayedProfiles = profiles;
  if (activeTab === 'friends') {
    displayedProfiles = profiles.filter(p => friendsList.includes(p.id) || p.id === currentProfile?.id);
  }

  return (
    <div>
      <div className="flex items-center gap-2" style={{ marginBottom: '1.5rem' }}>
        <Trophy size={28} color="var(--primary-color)" />
        <h2 style={{ margin: 0 }}>Classement</h2>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button className={`btn ${activeTab === 'global' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setActiveTab('global')} style={{ flex: 1, minWidth: '100px' }}>
          <Trophy size={18} /> Global
        </button>
        <button className={`btn ${activeTab === 'friends' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setActiveTab('friends')} style={{ flex: 1, minWidth: '100px' }}>
          <Users size={18} /> Mes Amis
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {displayedProfiles.length === 0 ? (
          <div className="card text-center text-secondary">
            {activeTab === 'friends' ? "Tu n'as pas encore d'amis." : "Aucun profil trouvé."}
          </div>
        ) : (
          displayedProfiles.map((profile, index) => {
            const isMe = currentProfile && currentProfile.id === profile.id;
            const isFriend = friendsList.includes(profile.id);
            const hasSentRequest = sentRequests.includes(profile.id);
            const hasReceivedRequest = receivedRequests.includes(profile.id);
            
            let RankIcon = null;
            if (index === 0) RankIcon = <Medal size={24} color="#FFD700" />;
            else if (index === 1) RankIcon = <Medal size={24} color="#C0C0C0" />;
            else if (index === 2) RankIcon = <Medal size={24} color="#CD7F32" />;
            else RankIcon = <span style={{ width: '24px', textAlign: 'center', fontWeight: 'bold', color: 'var(--text-secondary)' }}>{index + 1}</span>;

            return (
              <React.Fragment key={profile.id}>
                <div 
                  className="card flex items-center leaderboard-user-card" 
                  style={{ 
                    padding: '0.75rem 0.6rem', 
                    gap: '0.6rem',
                    border: isMe ? '2px solid var(--primary-color)' : '1px solid var(--border-color)',
                    backgroundColor: isMe ? 'rgba(168, 159, 207, 0.05)' : 'var(--bg-card)',
                    boxShadow: isMe ? 'var(--shadow)' : 'none',
                    cursor: 'pointer',
                    flexWrap: 'nowrap',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  onClick={() => handleExpandProfile(profile.id)}
                >
                  <div className="leaderboard-rank-icon" style={{ width: '24px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {RankIcon}
                  </div>

                  <div className="leaderboard-avatar-col" style={{ flexShrink: 0 }}>
                    <UserAvatar profile={profile} size={42} onClick={(e) => { e.stopPropagation(); navigate(`/profile/${profile.id}`); }} />
                  </div>

                  <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <h3 
                      style={{ 
                        margin: 0, 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.4rem', 
                        fontSize: '0.95rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden'
                      }}
                      onClick={(e) => { e.stopPropagation(); navigate(`/profile/${profile.id}`); }}
                      title="Voir le profil"
                      onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                      onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profile.name}</span>
                      {isMe && <span style={{ fontSize: '0.65rem', backgroundColor: 'var(--primary-color)', color: '#fff', padding: '0.1rem 0.35rem', borderRadius: '10px', flexShrink: 0 }}>TOI</span>}
                    </h3>
                    <div className="leaderboard-user-meta" style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <strong style={{ color: 'var(--secondary-color)' }}>Niveau {profile.level}</strong> • {profile.grade}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginLeft: 'auto', flexShrink: 0 }}>
                    {!isMe && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {isFriend ? (
                          null
                        ) : hasReceivedRequest ? (
                          <button className="btn-icon" title="Demande reçue. Allez dans Social." disabled style={{ opacity: 0.6, padding: '4px' }}>
                            <UserCheck size={18} />
                          </button>
                        ) : hasSentRequest ? (
                          <button className="btn-icon" title="Annuler la demande" onClick={(e) => handleCancelRequest(e, profile.id)} style={{ color: 'var(--text-secondary)', padding: '4px' }}>
                            <Clock size={18} />
                          </button>
                        ) : (
                          <button className="btn-icon" title="Ajouter en ami" onClick={(e) => handleSendRequest(e, profile.id)} style={{ color: 'var(--primary-color)', padding: '4px' }}>
                            <UserPlus size={18} />
                          </button>
                        )}
                      </div>
                    )}

                    <div style={{ textAlign: 'right', minWidth: '42px' }}>
                      <div className="leaderboard-xp-val" style={{ fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.1 }}>{profile.totalXp}</div>
                      <div className="leaderboard-xp-lbl" style={{ fontSize: '0.65rem', color: 'var(--text-secondary)' }}>XP Total</div>
                    </div>
                    
                    <div style={{ padding: '0.1rem', display: 'flex', alignItems: 'center' }}>
                      {expandedProfileId === profile.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                  </div>
                </div>
                
                {/* EXPANDED SECTION */}
                {expandedProfileId === profile.id && (
                  <div className="card" style={{ marginTop: '-0.5rem', borderTopLeftRadius: 0, borderTopRightRadius: 0, backgroundColor: 'var(--bg-color)', border: '1px solid var(--border-color)', borderTop: 'none', padding: '1rem', animation: 'fadeIn 0.2s ease-out' }}>
                    {expandedData.loading ? (
                      <div className="text-center" style={{ fontSize: '0.85rem' }}>Chargement...</div>
                    ) : (
                      <div style={{ marginTop: '1.5rem' }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Badges Obtenus ({getEarnedBadges(expandedData.workouts, expandedData.goals, profile).length})</h4>
                        <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.5rem', flexWrap: 'wrap' }}>
                          {getEarnedBadges(expandedData.workouts, expandedData.goals, profile).length === 0 ? (
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>Aucun badge pour le moment</span>
                          ) : (
                            getEarnedBadges(expandedData.workouts, expandedData.goals, profile).map(badge => (
                              <div key={badge.id} style={{ 
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: '36px', height: '36px', borderRadius: '50%', 
                                backgroundColor: 'var(--bg-color)', border: '1px solid var(--border-color)',
                                fontSize: '1.2rem'
                              }} title={badge.name}>
                                {badge.name.split(' ').pop()}
                              </div>
                            ))
                          )}
                        </div>
                        
                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Ses Programmes</h4>
                        {expandedData.routines.length === 0 ? (
                          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Aucun programme partagé.</span>
                        ) : (
                          <div className="leaderboard-routines-grid">
                            {expandedData.routines.map(routine => (
                              <div 
                                key={routine.id} 
                                className="card flex justify-center items-center flex-col text-center" 
                                style={{ 
                                  backgroundColor: 'var(--bg-card)', 
                                  padding: '0.8rem 0.4rem', 
                                  borderRadius: 'var(--border-radius-sm)', 
                                  border: '1px solid var(--border-color)',
                                  cursor: 'pointer',
                                  transition: 'transform 0.1s',
                                  gap: '0.4rem',
                                  minWidth: 0,
                                  width: '100%',
                                  boxSizing: 'border-box'
                                }}
                                onClick={(e) => { e.stopPropagation(); setSelectedRoutine({ ...routine, isMe }); }}
                                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                              >
                                <strong style={{ fontSize: '0.85rem', width: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{routine.name}</strong>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{routine.exercises?.length || 0} exos</div>
                                
                                {!isMe && currentProfile && (
                                  <button 
                                    className="btn leaderboard-clone-btn" 
                                    style={{ display: 'flex', gap: '0.3rem', justifyContent: 'center', alignItems: 'center', backgroundColor: clonedRoutines[routine.id] ? 'var(--secondary-color)' : 'var(--primary-color)', color: clonedRoutines[routine.id] ? '#121016' : '#fff', boxSizing: 'border-box' }}
                                    onClick={(e) => { e.stopPropagation(); handleCloneRoutine(routine); }}
                                    disabled={clonedRoutines[routine.id]}
                                  >
                                    {clonedRoutines[routine.id] ? <Check size={14} /> : <Copy size={14} />}
                                    <span>{clonedRoutines[routine.id] ? 'Cloné' : 'Cloner'}</span>
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })
        )}
      </div>

      {/* Routine Details Modal */}
      {selectedRoutine && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSelectedRoutine(null)}>
          <div className="card" style={{ width: '90%', maxWidth: '400px', maxHeight: '80vh', overflow: 'hidden', padding: '1.5rem', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4" style={{ flexShrink: 0 }}>
              <h3 style={{ margin: 0 }}>{selectedRoutine.name}</h3>
              <button className="btn-icon" onClick={() => setSelectedRoutine(null)}><X size={24} /></button>
            </div>
            
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: 'var(--text-secondary)', flexShrink: 0 }}>Exercices</h4>
            {selectedRoutine.exercises && selectedRoutine.exercises.length > 0 ? (
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', marginBottom: '1rem', paddingRight: '0.5rem' }}>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {selectedRoutine.exercises.map((ex, i) => {
                    if (ex.type === 'superset') {
                      return (
                        <li key={i} style={{ padding: '0.8rem', backgroundColor: 'rgba(168, 159, 207, 0.05)', borderRadius: 'var(--border-radius-sm)', border: '1px dashed var(--primary-color)' }}>
                          <div style={{ fontSize: '0.7rem', color: 'var(--primary-color)', marginBottom: '0.3rem', fontWeight: 'bold' }}>SUPERSET</div>
                          {ex.items && ex.items.map((subEx, j) => (
                            <strong key={j} style={{ display: 'block', fontSize: '0.95rem', marginTop: j > 0 ? '0.5rem' : 0 }}>
                              {subEx.name}
                            </strong>
                          ))}
                        </li>
                      );
                    }
                    return (
                      <li key={i} style={{ padding: '0.8rem', backgroundColor: 'var(--bg-color)', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)' }}>
                        <strong style={{ display: 'block', fontSize: '0.95rem' }}>{ex.name}</strong>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <div className="text-center text-secondary" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Aucun exercice dans ce programme.</div>
            )}

            {!selectedRoutine.isMe && currentProfile && (
              <button 
                className="btn btn-primary" 
                style={{ width: '100%', marginTop: 'auto', display: 'flex', justifyContent: 'center', gap: '0.5rem', padding: '0.8rem', flexShrink: 0 }}
                onClick={(e) => { e.stopPropagation(); handleCloneRoutine(selectedRoutine); setSelectedRoutine(null); }}
                disabled={clonedRoutines[selectedRoutine.id]}
              >
                {clonedRoutines[selectedRoutine.id] ? <Check size={18} /> : <Copy size={18} />}
                {clonedRoutines[selectedRoutine.id] ? 'Programme cloné' : 'Cloner ce programme'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
